import { useState, useEffect, useCallback } from 'react'
import { getDoctorsService } from '../../../../services/Services'
import { type DoctorType } from '../../../../model/doctor'
import { useNavigate } from 'react-router-dom'

const INITIAL_DOCTOR_PROFILE: DoctorType = {
  isOnboarded: false,
  requestedContact: false,
  locale: 'en-us' as const,
  dossierPreferences: {
    displayMode: 'TIMELINE',
    sortOrder: 'DESC'
  },
  schemaId: null,
  schema: {
    history: {
      displayName: '',
      fields: []
    },
    visit: {
      displayName: '',
      fields: []
    }
  },
  phone: '',
  isSubscribed: false,
  visitPrice: 0,
  followUpPrice: 0,
  firstName: '',
  lastName: '',
  gender: 'M',
  planExpiresAt: 0,
  address: '',
  city: '',
  freeMaxUsage: 0,
  statistics: {
    patients: {
      total: 0,
      todayCounter: 0,
      todayDate: 0
    },
    visits: {
      total: 0,
      todayCounter: 0,
      todayDate: 0
    },
    followUps: {
      total: 0,
      todayCounter: 0,
      todayDate: 0
    }
  }
}

interface UseDoctorProfileOptions {
  ownerId: string | null | undefined
  suppressOnboardingRedirect?: boolean
}

const useDoctorProfile = ({
  ownerId,
  suppressOnboardingRedirect = false
}: UseDoctorProfileOptions) => {
  const [doctorProfile, setDoctorProfile] = useState<DoctorType>(
    INITIAL_DOCTOR_PROFILE
  )

  const navigate = useNavigate()

  const [loading, setLoading] = useState<boolean>(true)

  const fetchDoctorProfile = useCallback(async () => {
    if (!ownerId) {
      // Role resolution briefly leaves the effective owner unknown after sign-in.
      // Keep the profile pending so subscription guards never evaluate the empty
      // initial profile and redirect an entitled doctor to the frozen route.
      setLoading(true)
      return
    }

    setLoading(true)

    const profile = await getDoctorsService().getDoctorProfile({
      ownerId
    })

    if (!profile?.isOnboarded && !suppressOnboardingRedirect) {
      navigate('/onboarding')
      return
    }

    if (profile) {
      setDoctorProfile(profile)
    }
    setLoading(false)
  }, [navigate, ownerId, suppressOnboardingRedirect])

  useEffect(() => {
    fetchDoctorProfile()
  }, [fetchDoctorProfile])

  return {
    ...doctorProfile,
    loading
  }
}

export default useDoctorProfile
